import React from 'react';
import { Text } from '@chatsdet/ui';
import type { ExternalTestResultOutput } from '@chatsdet/types';
import { CopyablePre } from '../../components/CopyButton';

/**
 * One labelled, copyable, scroll-bounded text block. Reused for stdout,
 * stderr, and each text attachment so long provider output never blows out
 * the sidebar's layout — height is capped, lines wrap, and the copy
 * affordance follows the same pattern as the rest of the app's log/code
 * blocks (`CopyablePre`).
 */
function OutputBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="mb-2 last:mb-0">
      <Text size="xs" className="mb-1 block text-muted-foreground">
        {label}
      </Text>
      <CopyablePre value={value} label={label}>
        <pre className="max-h-48 overflow-y-auto whitespace-pre-wrap break-all rounded bg-muted/30 p-2 font-mono text-[11px] leading-relaxed text-muted-foreground">
          {value}
        </pre>
      </CopyablePre>
    </div>
  );
}

/**
 * A test's own stdout/stderr and text attachments — kept server-side for
 * FAILED attempts only, so this renders nothing for a passing attempt (and
 * nothing for a provider or older row that never captured output at all).
 * Content is customer text from the test's own process, rendered as text
 * only — never HTML or markdown.
 *
 * The caller passes output only from the currently selected attempt. The
 * section never borrows output from an earlier retry.
 */
export function ExternalTestOutputSection({
  output,
}: {
  output: ExternalTestResultOutput | null | undefined;
}) {
  if (!output) return null;
  const hasContent = Boolean(output.stdout || output.stderr || output.attachments?.length);
  if (!hasContent) return null;

  return (
    <div className="space-y-2 px-2 py-1">
      {output.stdout ? <OutputBlock label="stdout" value={output.stdout} /> : null}
      {output.stderr ? <OutputBlock label="stderr" value={output.stderr} /> : null}
      {output.attachments?.map((attachment, index) => (
        <OutputBlock
          key={`${attachment.name}-${index}`}
          label={`${attachment.name} · ${attachment.contentType}`}
          value={attachment.body}
        />
      ))}
    </div>
  );
}
